/**
 * PromReadyPage — /prom/:slug/ready
 *
 * This is NOT a confirmation page. It is a launch button.
 *
 * Single goal: get the teacher to share the QR/link with their
 * students RIGHT NOW. Everything is in service of that one action.
 *
 *   ✅ Confirmation
 *   🔥 Instruction (most important line)
 *   📱 QR (dominant)
 *   🚀 WhatsApp share (primary)
 *   📋 Copy link (with "paste into class group" toast)
 *   💬 Reassurance + soft preview link
 *   £199 activate as a quiet footer card (does not compete)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, Copy, ArrowRight, WhatsappLogo, Envelope, Eye, Confetti, Flame } from '@phosphor-icons/react';
import { AnimatePresence } from 'framer-motion';
import UpsellBanner from '../components/UpsellBanner';
import { gradient as featureGradient, gradientPair } from '../lib/featureGradients';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POST_SHARE_DELAY_MS = 1200;

export default function PromReadyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [guestCount, setGuestCount] = useState(null);
  const [showShareUpsell, setShowShareUpsell] = useState(false);

  const link = typeof window !== 'undefined' ? `${window.location.origin}/e/${slug}` : '';
  const shareMessage = `Join our prom 🎉\nUpload photos, videos & messages here:\n${link}`;

  // Fetch live student-upload count once on mount. On failure, keep the
  // count at 0 so the "Be the first to share" fallback still renders —
  // the hint should ALWAYS appear, just with the right copy.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ev = await axios.get(`${API}/events/slug/${slug}`);
        const eventId = ev?.data?.event_id;
        if (!eventId) {
          if (!cancelled) setGuestCount(0);
          return;
        }
        const res = await axios.get(`${API}/events/${eventId}/public-stats`);
        if (!cancelled) setGuestCount(res?.data?.guests ?? 0);
      } catch (e) {
        if (!cancelled) setGuestCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const triggerPostShareUpsell = () => {
    // Delay slightly so the user sees the toast/share UI first, then
    // the upsell rises after their attention has settled.
    setTimeout(() => setShowShareUpsell(true), POST_SHARE_DELAY_MS);
    setTimeout(() => setShowShareUpsell(false), POST_SHARE_DELAY_MS + 8000);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied — now paste this into your class WhatsApp group');
      setTimeout(() => setCopied(false), 2500);
      triggerPostShareUpsell();
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    triggerPostShareUpsell();
  };

  const shareEmail = () => {
    const subject = encodeURIComponent('Our prom — join here');
    const body = encodeURIComponent(shareMessage);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    triggerPostShareUpsell();
  };

  const onActivate = () => {
    navigate(`/login?from=/prom/${slug}/activate`);
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white px-5 py-10" data-testid="prom-ready">
      <div className="max-w-md mx-auto flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center w-full"
        >
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-3">
            <CheckCircle size={24} weight="fill" className="text-emerald-300" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black mb-2 flex items-center justify-center gap-2" data-testid="prom-ready-title">
            Your prom is ready <Confetti size={22} weight="fill" className="text-amber-300" />
          </h1>
          {/* THE INSTRUCTION — most important line on the page */}
          <p className="text-white/85 text-sm sm:text-base mb-3 leading-relaxed" data-testid="prom-ready-instruction">
            Share this with students now — they can start uploading instantly
          </p>

          {/* Live activity hint OR first-mover urgency */}
          {guestCount !== null && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.04] border border-white/8 text-[12px] text-white/70 mb-6" data-testid="prom-ready-activity">
              {guestCount > 0 ? (
                <><Flame size={12} weight="fill" className="text-amber-300" /> {guestCount} student{guestCount === 1 ? '' : 's'} already joined</>
              ) : (
                <>Be the first to share this with your students</>
              )}
            </div>
          )}
        </motion.div>

        {/* QR — dominant, no decoration; shadow tinted with the QR Upload
            brand gradient so it visually echoes the launch button. */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="rounded-2xl bg-white p-4 mb-3"
          style={{
            boxShadow: `0 20px 50px -10px ${gradientPair('qr_upload').to}40, 0 8px 25px -5px ${gradientPair('qr_upload').from}30`,
          }}
        >
          <img
            src={`${API}/prom/${slug}/qr`}
            alt="Prom QR code"
            className="w-56 h-56 block"
            data-testid="prom-ready-qr"
          />
        </motion.div>
        <p className="text-xs text-white/50 mb-6 text-center" data-testid="prom-ready-qr-caption">
          Students just scan and start — no apps needed
        </p>

        {/* PRIMARY: WhatsApp — fastest path to activation */}
        <button
          onClick={shareWhatsApp}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-black text-black bg-[#25D366] hover:bg-[#1fb858] transition-colors shadow-lg mb-3"
          data-testid="prom-ready-whatsapp"
        >
          <WhatsappLogo size={18} weight="fill" /> Share on WhatsApp <ArrowRight size={14} weight="bold" />
        </button>

        {/* SECONDARY: Copy link */}
        <button
          onClick={onCopy}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-black text-black bg-white hover:bg-neutral-200 transition-colors mb-3"
          data-testid="prom-ready-copy"
        >
          <Copy size={14} weight="bold" /> {copied ? 'Copied — paste into your class group' : 'Copy link'}
        </button>

        {/* TERTIARY: Email — subtle, optional */}
        <button
          onClick={shareEmail}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white/85 border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
          data-testid="prom-ready-email"
        >
          <Envelope size={14} weight="fill" /> Send via email
        </button>

        {/* Truncated share link — readable but not the focus */}
        <p className="text-[11px] text-white/35 mt-4 text-center break-all w-full px-2" data-testid="prom-ready-link">
          {link}
        </p>

        {/* Reassurance */}
        <p className="text-xs text-white/55 mt-6 text-center max-w-xs" data-testid="prom-ready-reassurance">
          That&apos;s it — no setup needed. Students can start using it immediately.
        </p>

        {/* Soft preview link — never a button */}
        <a
          href={`/e/${slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[12px] text-[#C29D52]/80 hover:text-[#C29D52]/85 mt-4 underline-offset-2 hover:underline"
          data-testid="prom-ready-preview"
        >
          <Eye size={13} weight="fill" /> Preview student view
        </a>

        {/* Activation card — quiet enough not to compete with sharing,
            but visually anchored with the brand gradient so it reads as
            "the upgrade" not "an afterthought". */}
        <div className="w-full mt-10 pt-6 border-t border-white/8">
          <div className="rounded-xl p-4 bg-white/[0.03] border border-white/8 text-center" data-testid="prom-ready-activate-card">
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.18em] text-white mb-2"
              style={{ background: featureGradient('pro') }}
            >
              <Confetti size={10} weight="fill" /> Most popular
            </div>
            <p className="text-[14px] font-black text-white mb-1">Unlock the full prom — £199</p>
            <p className="text-[12px] text-white/55 mb-3">
              Unlimited uploads · No watermark · Highlight reel · Memory book · Premium support
            </p>
            <button
              onClick={onActivate}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-black text-white shadow-md hover:opacity-95 transition-opacity"
              style={{ background: featureGradient('pro') }}
              data-testid="prom-ready-activate"
            >
              Unlock everything <ArrowRight size={12} weight="bold" />
            </button>
            <p className="text-[10px] text-white/35 mt-2">One-off · No subscription · Refundable in 24h</p>
          </div>
        </div>
      </div>

      {/* Post-share upsell — peak emotional state for the teacher who
          just shared with their class. Slides up, dismisses on its own. */}
      <AnimatePresence>
        {showShareUpsell && (
          <UpsellBanner
            source="prom_ready_post_share"
            variant="toast"
          />
        )}
      </AnimatePresence>
    </div>
  );
}
